import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import Button from './Button';
import Pagination from './Pagination';
import useDebouncedValue from '../hooks/useDebouncedValue';

const SOURCE_META = {
  manual: { label: 'Manual', badge: 'text-bg-secondary' },
  version: { label: 'By version', badge: 'text-bg-warning' },
  cloaking: { label: 'Cloaking', badge: 'text-bg-danger' },
};

const SOURCE_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'manual', label: 'Manual' },
  { key: 'version', label: 'By version' },
  { key: 'cloaking', label: 'Cloaking' },
];

function formatDateTime(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function RuleSection({ config, onError, onMutated, refreshToken }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [query, setQuery] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);

  const debouncedQuery = useDebouncedValue(query);
  const requestIdRef = useRef(0);

  useEffect(() => {
    setPage(1);
  }, [debouncedQuery, sourceFilter, pageSize]);

  const fetchPage = useCallback(async () => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    setLoading(true);
    try {
      const res = await axios.get('/api/android-id-rules', {
        params: {
          listType: config.type,
          page,
          pageSize,
          q: debouncedQuery || undefined,
          source: config.showSourceFilter && sourceFilter !== 'all' ? sourceFilter : undefined,
        },
      });

      if (requestIdRef.current !== requestId) return;

      const data = res.data || {};
      setItems(data.items || []);
      setTotal(data.total || 0);
      setTotalPages(data.totalPages || 1);
      if (data.page > data.totalPages) setPage(data.totalPages);
    } catch (err) {
      if (requestIdRef.current === requestId) onError(err);
    } finally {
      if (requestIdRef.current === requestId) setLoading(false);
    }
  }, [config.type, config.showSourceFilter, page, pageSize, debouncedQuery, sourceFilter, onError]);

  useEffect(() => {
    fetchPage();
  }, [fetchPage, refreshToken]);

  const submit = async (e) => {
    e.preventDefault();
    const androidId = value.trim();
    if (!androidId || busy) return;

    setBusy(true);
    try {
      await axios.post('/api/android-id-rules', { androidId, listType: config.type });
      setValue('');
      onMutated();
    } catch (err) {
      onError(err);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`/api/android-id-rules/${id}`);
      onMutated();
    } catch (err) {
      onError(err);
    }
  };

  const isFiltered = Boolean(debouncedQuery) || sourceFilter !== 'all';

  return (
    <div className="app-editor-card compact-card settings-card">
      <div className="app-editor-card-body compact-card-body">
        <div className="settings-card-head">
          <div className="stats-section-title">{config.title}</div>
          <div className="dashboard-badge">{total}</div>
        </div>

        <div className="settings-toolbar">
          <form className="settings-add-row" onSubmit={submit}>
            <input
              type="text"
              className="form-control"
              placeholder={config.placeholder}
              value={value}
              onChange={(e) => setValue(e.target.value)}
            />
            <Button
              type="submit"
              variant={config.addVariant}
              className="dashboard-btn flex-shrink-0"
              disabled={busy || !value.trim()}
            >
              Add
            </Button>
          </form>

          <input
            type="text"
            className="form-control settings-search"
            placeholder="Search by Android ID"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />

          {config.showSourceFilter && (
            <div className="stats-chip-grid">
              {SOURCE_FILTERS.map((filter) => (
                <button
                  key={filter.key}
                  type="button"
                  className={`stats-chip ${sourceFilter === filter.key ? 'is-active' : ''}`}
                  onClick={() => setSourceFilter(filter.key)}
                >
                  {filter.label}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="settings-list">
          {loading && items.length === 0 ? (
            <div className="settings-empty">Loading…</div>
          ) : items.length === 0 ? (
            <div className="settings-empty">{isFiltered ? 'Nothing found' : 'List is empty'}</div>
          ) : (
            <div className="stats-table-wrap" style={{ opacity: loading ? 0.55 : 1 }}>
              <table className="rule-table">
                <thead>
                  <tr>
                    <th>Android ID</th>
                    <th className="text-end" style={{ width: '90px' }}>&nbsp;</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((rule) => {
                    const meta = SOURCE_META[rule.source] || SOURCE_META.manual;
                    const context = [
                      { label: 'App', value: rule.appId },
                      { label: 'AdId', value: rule.adId },
                      { label: 'IP', value: rule.ip },
                      { label: 'Ver', value: rule.appVersion },
                    ].filter((item) => item.value);

                    return (
                      <tr key={rule.id}>
                        <td>
                          <div className="d-flex flex-wrap align-items-center gap-2">
                            <span className="rule-value">{rule.androidId}</span>
                            {config.showSourceFilter && (
                              <span className={`badge rounded-pill ${meta.badge}`}>{meta.label}</span>
                            )}
                          </div>

                          {context.length > 0 && (
                            <div className="rule-context">
                              {context.map((item) => (
                                <span className="rule-context-item" key={item.label}>
                                  <span className="rule-context-key">{item.label}</span>
                                  <span className="rule-context-val">{item.value}</span>
                                </span>
                              ))}
                            </div>
                          )}

                          <div className="rule-meta">{formatDateTime(rule.createdAt)}</div>
                        </td>
                        <td className="text-end align-top">
                          <Button
                            variant="outline-danger"
                            size="sm"
                            className="dashboard-btn-sm"
                            onClick={() => remove(rule.id)}
                          >
                            Delete
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="settings-card-foot">
          <Pagination
            page={page}
            totalPages={totalPages}
            total={total}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            disabled={loading}
          />
        </div>
      </div>
    </div>
  );
}
